<template>
  <div class="container">
    <MessagesContainer
      :class="{ 'fixed left-2 right-2 top-2 z-50': currentStoreStore.isMobile }"
    />
  </div>

  <!-- Move product timer to header on desktop -->
  <Teleport to="header">
    <div class="my-2 hidden md:block">
      <ProductTimer />
    </div>
  </Teleport>

  <ProductDetailPage
    :class="showProductDetailPage ? 'visible' : 'invisible'"
    :is-simple-product="false"
  />
  <ConfiguratorOverlay :class="showConfigurator ? 'visible' : 'invisible'" />
  <ExtraOptionOverlay :class="showExtraOptionsPage ? 'visible' : 'invisible'" />

  <FullPageLoader
    v-if="configuratorStore.showFullPageLoader"
    :done="configuratorStore.isConfiguratorOpen"
    :current-step="currentStep"
    @destroy-me="configuratorStore.showFullPageLoader = false"
  />
</template>

<script lang="ts" setup>
import ProductTimer from '@/components/ProductTimer.vue';
import FullPageLoader from '@/components/States/FullPageLoader.vue';
import { useConfiguratorStore } from '@/stores/Configurator/ConfiguratorStore.ts';
import { computed, onBeforeMount, onMounted, onUnmounted, watch } from 'vue';
import I18n from '@/i18n.ts';
import MessagesContainer from '@/components/Messages/MessagesContainer.vue';
import ConfiguratorOverlay from '@/pages/ProductPage/partials/Configurator/ConfiguratorOverlay.vue';
import ExtraOptionOverlay from '@/pages/ProductPage/partials/ExtraOptions/ExtraOptionOverlay.vue';
import { ConfiguratorStep } from '@/types/Configurator/ConfiguratorStep.ts';
import ProductDetailPage from '@/pages/ProductPage/partials/ProductDetailPage/ProductDetailPage.vue';
import { useProductPage } from '@/composables/ProductPage.ts';
import { useContinueToken } from '@/composables/UseContinueToken.ts';
import { useCurrentStoreStore } from '@/stores/CurrentStore/CurrentStoreStore.ts';
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core';
import { useMediaclipCommunication } from '@/composables/UseMediaclipCommunication.ts';

const configuratorStore = useConfiguratorStore();
const productPage = useProductPage();
const currentStoreStore = useCurrentStoreStore();

useContinueToken().watchForChanges();

const mediaQuery = window.matchMedia('(max-width: 767px)');
const currentStep = computed(() => {
  return I18n.global.t(
    configuratorStore.isSimpleConfigurator ||
      configuratorStore.isLetterCollageConfigurator
      ? 'loader.currentstep.starting_tool_simple'
      : 'loader.currentstep.starting_tool'
  );
});

const showProductDetailPage = computed(
  () => Number(configuratorStore.currentStep) === ConfiguratorStep.ProductPage
);
const showConfigurator = computed(() =>
  [ConfiguratorStep.Configurator, ConfiguratorStep.HalfConfigurator].includes(
    Number(configuratorStore.currentStep)
  )
);
const showExtraOptionsPage = computed(
  () => Number(configuratorStore.currentStep) === ConfiguratorStep.ExtraOptions
);

const browserNavigationHandler = (() => {
  let isHandling = false;

  return (event: Event) => {
    if (isHandling) {
      return;
    }
    isHandling = true;

    event.preventDefault();

    // Set the configurator step
    if (showExtraOptionsPage.value) {
      configuratorStore.setConfiguratorStep();
      isHandling = false;
      return;
    }

    // Set the product page
    if (showConfigurator.value) {
      configuratorStore.currentStep = ConfiguratorStep.ProductPage;
      isHandling = false;
      return;
    }

    // Go back in history
    if (showExtraOptionsPage.value) {
      window.history.back();
    }

    isHandling = false;
  };
})();

function getPage(): HTMLBodyElement {
  const pageWrapper = document.querySelector('#html-body') as HTMLBodyElement;

  if (!pageWrapper) {
    throw new Error("Can't find page wrapper, something is very wrong");
  }

  return pageWrapper;
}

function screenSizeCheck() {
  configuratorStore.isMobile =
    useBreakpoints(breakpointsTailwind).isSmallerOrEqual('sm');
}

// Initial screen size check
onBeforeMount(screenSizeCheck);

onMounted(() => {
  if (
    configuratorStore.fullscreenStates.includes(
      configuratorStore.currentStep.valueOf()
    )
  ) {
    productPage.hideProductPage(getPage());
    useMediaclipCommunication().requestToken();
  }

  mediaQuery.addEventListener('change', screenSizeCheck);

  window.history.replaceState(null, '', window.location.href);
  window.addEventListener('popstate', browserNavigationHandler);
});

onUnmounted(() => {
  mediaQuery.removeEventListener('change', screenSizeCheck);
  window.removeEventListener('popstate', browserNavigationHandler);
});

watch(
  () => configuratorStore.currentStep,
  (newOpen: number, oldOpen: number) => {
    const wasFullscreen = configuratorStore.fullscreenStates.includes(oldOpen);
    const isFullscreen = configuratorStore.fullscreenStates.includes(newOpen);

    // If we go from configurator to the product page, reset data (this fixes an issue with the exit warning in theme selector)
    if (
      newOpen === ConfiguratorStep.ProductPage &&
      oldOpen === ConfiguratorStep.Configurator
    ) {
      configuratorStore.resetData();
    }

    // Handle state management:
    if (newOpen > oldOpen) {
      window.history.pushState(null, '', window.location.href);
    }

    // If configurator opened
    if (wasFullscreen && isFullscreen) {
      // Do nothing if we stay fullscreen
      return;
    }

    // Handle product page visibility
    const page = getPage();
    isFullscreen
      ? productPage.hideProductPage(page)
      : productPage.showProductPage(page);
  }
);
</script>
